import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[48ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[24ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

// HERO full-bleed (Sezione 2). Slot unico — l'utente la usa come "effetto wow".
const HERO_IMAGE =
  "/images/assets/w1v2ohmc_01.jpg";

// Galleria sezione 7 — 2 slot, mapping 1:1 stretto. Anti-duplicazione: niente riuso di HERO_IMAGE.
const GALLERY_PHOTOS = [
  // 02 — Ritratto di giocatrice seduta in tribuna con palla Mikasa e scarpe Mizuno rosse
  "/images/assets/ietryj7z_5c2207a8-4355-4678-8185-0eb1a9453e2c-980x1307.jpg",
  // 03 — Ritratto sorridente di giocatrice Chieri '76 in tribuna con palla Mikasa
  "/images/assets/uegm4e8n_1680687386761-980x1306.jpg",
];
const GALLERY_SLOTS = 2;
const LIGHTBOX_IMAGES = GALLERY_PHOTOS.map((src, i) => ({
  src,
  alt: `Your Next Level — Reale Mutua × Chieri '76 — Asset ${String(i + 2).padStart(2, "0")}`,
}));

const WorkRealeMutuaYourNextLevel = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxIndex]);

  const idx = WORKS.findIndex((w) => w.to === "/works/reale-mutua-your-next-level");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = idx >= 0 ? WORKS[(idx + 1) % WORKS.length] : WORKS[0];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Reale Mutua × Your Next Level", url: "/works/reale-mutua-your-next-level" },
  ]);

  return (
    <>
      <Seo
        title="Reale Mutua × Chieri 76: CEV Challenge Cup 2023 | OnSports Case"
        description="Attivazione sponsorship Reale Mutua per la finale CEV Challenge Cup di Chieri Volley, marzo 2023. Storytelling del sogno di un'intera città. OnSports."
        path="/works/reale-mutua-your-next-level"
        image="/images/assets/w1v2ohmc_01.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/reale-mutua-your-next-level/hero.jpg"
        imageAlt="Your Next Level — Reale Mutua × Chieri '76"
        eyebrow="● Case study · Cultural Impact · Brand Journalism · Content"
        titleLines={["Una stagione. Un viaggio.", "Un'EUROPA."]}
        highlightWord="EUROPA."
        subtitle="Reale Mutua Fenera Chieri '76 ha conquistato il suo primo trofeo europeo il 22 marzo 2023 a Timisoara, vincendo la CEV Challenge Cup. Abbiamo seguito quel momento per il main sponsor Reale Mutua Assicurazioni con un progetto di reportage editoriale: ritratti pre-gara delle giocatrici, trasferta in Romania, documentazione completa della finale di ritorno e un video emozionale che ha chiuso la stagione raccontandone i valori."
        meta="2023  ·  Cliente — Reale Mutua Assicurazioni  ·  Categoria — Brand Journalism & Reportage"
        overlayOpacity={0.45}
      />

      {/* SEZ. 2 — HERO IMAGE FULL-BLEED */}
      <section className="bg-dodgers" data-testid="section-hero-image">
        <RevealOnScroll>
          {HERO_IMAGE ? (
            <img
              src={HERO_IMAGE}
              alt="Timisoara, 22 marzo 2023 — Reale Mutua Fenera Chieri '76 alla finale di ritorno della CEV Challenge Cup"
              className="block w-screen object-cover"
              style={{ height: "clamp(40vh, 60vh, 70vh)", objectPosition: "center" }}
              loading="lazy"
              draggable="false"
              data-testid="hero-image-full"
            />
          ) : (
            <div
              className="w-screen bg-dodgers border-y border-lime/30 flex items-center justify-center"
              style={{ height: "clamp(40vh, 60vh, 70vh)" }}
              data-testid="hero-image-placeholder"
            >
              <span className="font-condensed uppercase tracking-widest text-[14px] text-galactico">
                Hero image in arrivo
              </span>
            </div>
          )}
        </RevealOnScroll>
        <div className="container-os pt-4 pb-12 md:pb-16">
          <p
            className="text-[13px] leading-[1.55] text-mandrogni opacity-80"
            style={{ fontStyle: "italic" }}
          >
            Timisoara, 22 marzo 2023. La finale di ritorno della CEV Challenge Cup.
          </p>
        </div>
      </section>

      {/* SEZ. 3 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    Il 22 marzo 2023, allo Sala Polivalentă di Timisoara, la
                    Reale Mutua Fenera Chieri '76 batte il CSM Lugoj e conquista
                    la CEV Challenge Cup. Per il club piemontese — fondato nel
                    2009, al debutto stagionale in una competizione
                    continentale — è il primo trofeo europeo della propria
                    storia, raggiunto con un percorso netto di vittorie nelle
                    fasi precedenti.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Reale Mutua Assicurazioni è main sponsor della squadra
                    dalla stagione 2019/20. La partnership ha visto crescere il
                    club anno dopo anno fino a questo traguardo, e quando la
                    finale di Challenge Cup è arrivata, il brand ci ha chiesto
                    di costruire un dispositivo editoriale capace di raccontare
                    quel momento secondo il proprio territorio narrativo: Your
                    Next Level — Alza il livello. Pallone dopo pallone, partita
                    dopo partita, stagione dopo stagione.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.25}>
                  <p>
                    Abbiamo gestito un progetto di reportage editoriale a due
                    tempi: una prima fase di produzione a Chieri nei giorni
                    precedenti alla finale di ritorno — ritratti delle
                    giocatrici, interviste, costruzione del backstory; una
                    seconda fase on-site in Romania per la documentazione
                    integrale della trasferta e della partita. Da questi due
                    set di riprese è nato un video emozionale finale di
                    sintesi, distribuito sui canali Reale Mutua nelle settimane
                    successive alla vittoria.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">
                      Your Next Level — Reportage Chieri '76 Timisoara
                    </SchedaRow>
                    <SchedaRow label="Cliente">
                      Reale Mutua Assicurazioni
                    </SchedaRow>
                    <SchedaRow label="Property">
                      Reale Mutua Fenera Chieri '76
                    </SchedaRow>
                    <SchedaRow label="Ruolo">
                      Concept editoriale · Produzione foto e video<br />
                      Trasferta e logistica · Post-produzione
                    </SchedaRow>
                    <SchedaRow label="Evento">
                      CEV Challenge Cup, finale di ritorno
                    </SchedaRow>
                    <SchedaRow label="Data">
                      22 marzo 2023 · Timisoara, Romania
                    </SchedaRow>
                    <SchedaRow label="Avversario">CSM Lugoj</SchedaRow>
                    <SchedaRow label="Risultato">
                      Vittoria — 1° trofeo europeo del Chieri '76
                    </SchedaRow>
                    <SchedaRow label="Pilastro">
                      Cultural Impact · Content &amp; IP Studio
                    </SchedaRow>
                    <SchedaRow label="Anno">2023</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Trasformare un evento sportivo in una storia di brand.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Una sponsorship sportiva ha la sua narrazione più potente nei
                  momenti epici. Una finale europea, il primo trofeo di un
                  club, una trasferta lontana — sono le situazioni in cui la
                  presenza del brand può smettere di essere un logo accanto al
                  gesto sportivo e diventare parte attiva del racconto. Ma
                  serve un dispositivo editoriale capace di intercettare quel
                  momento al punto giusto, con i ritmi giusti, e di tradurlo in
                  contenuto coerente con i valori del brand.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  Il rischio era quello tipico di queste situazioni: una
                  documentazione frammentata, decine di clip social senza un
                  filo conduttore, contenuti che si esauriscono nei due giorni
                  successivi alla partita. Reale Mutua aveva bisogno del
                  contrario — un video emozionale definitivo, riusabile per la
                  propria comunicazione istituzionale anche mesi dopo l'evento,
                  capace di dichiarare la propria vicinanza al club attraverso
                  la storia di una stagione intera. Per ottenerlo, era
                  necessario costruire il materiale narrativo prima ancora che
                  la partita iniziasse.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — IL VIAGGIO / VIDEO YOUTUBE */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-video">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Il viaggio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Da Chieri a Timisoara. Pallone dopo pallone.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Il video emozionale che ha chiuso il progetto. Costruito sui
                  ritratti pre-gara delle giocatrici a Chieri, sulle riprese
                  della trasferta in Romania, sui momenti chiave della finale e
                  sulle reazioni post-vittoria. Pensato per essere distribuito
                  sui canali Reale Mutua come messaggio identitario della
                  stagione 2022/23.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <RevealOnScroll delay={0.05}>
            <div className="mx-auto max-w-[1100px]" data-testid="video-ynl">
              <div className="aspect-video w-full overflow-hidden bg-dodgers/10">
                <iframe
                  src="https://www.youtube.com/embed/-Pt1pH8zKBA"
                  title="Your Next Level — Reale Mutua × Chieri '76"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  className="w-full h-full border-0"
                />
              </div>
              <p
                className="mt-4 text-[14px] leading-[1.55] text-mandrogni"
                style={{ fontStyle: "italic" }}
              >
                Your Next Level. Il video finale per i canali Reale Mutua
                Assicurazioni.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 6 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Due set di riprese. Una storia continua.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Abbiamo gestito il progetto end-to-end, dal concept iniziale
                  alla post-produzione del video finale. La regia ha tenuto
                  insieme due fasi di produzione fisicamente distanti — Chieri
                  e Timisoara — con un unico filo narrativo: la stagione del
                  Chieri come metafora del valore next level che il brand
                  voleva incarnare.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Concept editoriale"
                body="Definizione del territorio narrativo Your Next Level applicato al momento sportivo, costruzione della struttura del video emozionale come climax narrativo (dalla quotidianità degli allenamenti al trionfo finale), allineamento dei valori di brand (vicinanza, supporto, ambizione condivisa) con i codici del racconto sportivo."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Pre-gara · Chieri"
                body="Set di ritratti delle giocatrici nei giorni precedenti alla trasferta, interviste individuali, riprese degli allenamenti finali, atmosfera del Pala Gianni Asti dopo la vittoria della finale d'andata. È il materiale che ha permesso al video finale di non essere solo highlight di partita ma racconto di persone."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Trasferta · Timisoara"
                body="Logistica completa della trasferta in Romania, documentazione del viaggio della squadra, riprese al palazzetto Sala Polivalentă durante la finale di ritorno, capture dei momenti chiave del match e delle reazioni post-vittoria. Trasferta gestita in coordinamento con il club e con la federazione europea CEV."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <ApproccioBlock
                num="04"
                title="Post-produzione & distribuzione"
                body="Editing del video emozionale finale, sound design, color grading, versioning multi-formato per i diversi canali del brand. Distribuzione coordinata sui canali Reale Mutua Assicurazioni come contenuto identitario della stagione, riusabile in materiali istituzionali nei mesi successivi."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — GALLERIA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-gallery">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Visual</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Due momenti del progetto.</h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch] opacity-90">
                  Due scatti dal reportage: il backstage pre-gara e il momento
                  della vittoria.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gallery-grid">
            {Array.from({ length: GALLERY_SLOTS }).map((_, i) => {
              const photo = GALLERY_PHOTOS[i];

              if (!photo) {
                return (
                  <RevealOnScroll key={i} delay={i * 0.05}>
                    <div
                      className="block w-full relative bg-dodgers/[0.05] border border-lime border-dashed flex items-center justify-center"
                      style={{ aspectRatio: "4 / 3" }}
                      data-testid={`gallery-placeholder-${i}`}
                      aria-label={`Asset ${i + 2} — in arrivo`}
                    >
                      <span
                        className="font-condensed uppercase tracking-widest text-galactico"
                        style={{ fontSize: "12px" }}
                      >
                        Foto in arrivo
                      </span>
                    </div>
                  </RevealOnScroll>
                );
              }

              return (
                <RevealOnScroll key={i} delay={i * 0.05}>
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="block w-full overflow-hidden relative group focus:outline-none bg-dodgers/[0.04]"
                    style={{ aspectRatio: "4 / 3" }}
                    data-testid={`gallery-${i}`}
                    data-hover="true"
                    aria-label={`Apri asset ${i + 2}`}
                  >
                    <img
                      src={photo}
                      alt={LIGHTBOX_IMAGES[i].alt}
                      loading="lazy"
                      className="block w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                      draggable="false"
                    />
                    <span
                      className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </button>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 8 — IMPATTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Cosa è rimasto di quella stagione.
                </h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="1°" label="Trofeo europeo della storia del Chieri '76" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock
                value="22.03.2023"
                label="Finale di ritorno a Timisoara vs CSM Lugoj"
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="2" label="Set di riprese · Chieri + Romania" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock
                value="1"
                label="Video emozionale identitario per Reale Mutua"
              />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Reportage editoriale realizzato per Reale Mutua Assicurazioni, main
            sponsor della Reale Mutua Fenera Chieri '76 dalla stagione 2019/20.
            Documentazione integrale della CEV Challenge Cup 2022/23, vinta dal
            Chieri il 22 marzo 2023 a Timisoara contro il CSM Lugoj.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Sponsorship dentro una narrazione
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Il video finale non è stato un riepilogo del match, è stato
                  una dichiarazione identitaria del brand. Reale Mutua entra
                  nel racconto come parte costitutiva del percorso del club —
                  non come logo a margine ma come compagno di viaggio, in linea
                  con i valori della propria comunicazione istituzionale di
                  mutua centenaria.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Reportage che vale oltre la stagione
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Il materiale prodotto — foto, interviste, riprese on-site —
                  è diventato asset patrimoniale per Reale Mutua, riusabile
                  nei materiali istituzionali, nelle deck commerciali, nelle
                  attivazioni cross-canale degli anni successivi. La differenza
                  tra un contenuto stagionale e un contenuto patrimoniale è
                  esattamente questa: la possibilità di lavorare per il brand
                  oltre la finestra di lancio.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Un trofeo storico, raccontato come tale
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Per un club fondato nel 2009 e al debutto in una competizione
                  continentale, vincere la Challenge Cup era un evento storico.
                  Lo abbiamo raccontato con il peso editoriale adeguato,
                  evitando il tono generico del "video sponsorizzato" e
                  costruendo invece il documentario emozionale di una stagione
                  — il modello che la sponsorship sportiva contemporanea
                  richiede per produrre valore reale.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 9 — CREDITS */}
      <section className="py-20 md:py-28 bg-galactico text-dodgers" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Cliente">Reale Mutua Assicurazioni</SchedaRow>
                <SchedaRow label="Property">
                  Reale Mutua Fenera Chieri '76
                </SchedaRow>
                <SchedaRow label="Progetto">
                  Your Next Level — Reportage CEV Challenge Cup
                </SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Concept editoriale · Produzione foto e video<br />
                  Trasferta in Romania · Post-produzione
                </SchedaRow>
                <SchedaRow label="Servizi">
                  Content &amp; IP Studio · Sponsorship &amp; Impact Programs
                </SchedaRow>
                <SchedaRow label="Evento">
                  CEV Challenge Cup 2022/23 · Finale di ritorno<br />
                  Timisoara, 22 marzo 2023
                </SchedaRow>
                <SchedaRow label="Anno">2023</SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[30ch]">
              Hai una sponsorship sportiva che attraversa un momento storico?
              Costruiamo insieme il suo{" "}
              <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Lavoriamo con assicurazioni, banche e brand corporate che
              vogliono trasformare i grandi momenti delle property sportive che
              sostengono in materiale narrativo patrimoniale. Reportage
              editoriale, video emozionali, racconto end-to-end.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 11 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkRealeMutuaYourNextLevel;
